/**
 * adScheduler.js — Ad scheduling and media selection utilities
 *
 * Handles calculation of ad trigger times (percentage-based and custom_time),
 * media file selection with randomization, and building a sorted ad queue.
 */

/**
 * Parse "HH:MM:SS" duration string to seconds
 * @param {string} duration - Duration in "HH:MM:SS" format
 * @returns {number} Duration in seconds
 */
export function parseDuration(duration) {
    if (!duration || typeof duration !== "string") return 0;
    var parts = duration.split(":");
    if (parts.length !== 3) return 0;
    return (
        (parseInt(parts[0], 10) || 0) * 3600 +
        (parseInt(parts[1], 10) || 0) * 60 +
        (parseInt(parts[2], 10) || 0)
    );
}

/**
 * Calculate when an ad should trigger based on content duration
 * Supports both percentage_based and custom_time campaigns
 *
 * @param {object} creative - VastCreative from the VAST JSON response
 * @param {number} contentDurationSec - Content duration in seconds
 * @returns {number} Trigger time in seconds
 */
export function getAdTriggerTime(creative, contentDurationSec) {
    // Custom time takes priority (set by admin via customTimings)
    if (creative.extCustomTime !== undefined && creative.extCustomTime !== null) {
        var customTime = Number(creative.extCustomTime);
        if (Number.isFinite(customTime) && customTime >= 0) return customTime;
    }

    // Percentage-based from slot position
    var duration = Number.isFinite(contentDurationSec)
        ? Math.max(0, contentDurationSec)
        : 0;
    switch (creative.creativeSlot) {
        case "pre-roll":
            return 0;
        case "first-quartile":
            return Math.round(duration * 0.25);
        case "mid-roll":
            return Math.round(duration * 0.5);
        case "third-quartile":
            return Math.round(duration * 0.75);
        default:
            return 0;
    }
}

/**
 * Select media files from a creative, respecting capacity and randomization
 *
 * @param {object} creative - VastCreative object
 * @returns {Array<object>} Selected media files
 */
export function selectMediaFiles(creative) {
    if (
        !creative ||
        !creative.linear ||
        !creative.linear.mediaFiles ||
        creative.linear.mediaFiles.length === 0
    ) {
        return [];
    }

    var mediaFiles = creative.linear.mediaFiles.filter(function (media) {
        return normalizeAdMediaSources([media]).length === 1;
    });
    var capacityValue = Number(creative.extCapacity);
    var capacity =
        Number.isInteger(capacityValue) && capacityValue > 0
            ? Math.min(capacityValue, mediaFiles.length)
            : mediaFiles.length;

    if (creative.extRandomMedia && mediaFiles.length > 1) {
        // Fisher-Yates shuffle
        for (var i = mediaFiles.length - 1; i > 0; i--) {
            var j = Math.floor(Math.random() * (i + 1));
            var temp = mediaFiles[i];
            mediaFiles[i] = mediaFiles[j];
            mediaFiles[j] = temp;
        }
    }

    return mediaFiles.slice(0, capacity);
}

/**
 * Extract media URLs from selected media files
 * @param {object} creative - VastCreative object
 * @returns {Array<string>} URLs of selected media files
 */
export function getMediaUrls(creative) {
    return selectMediaFiles(creative).map(function (mf) {
        return mf.url;
    });
}

const MP4_MEDIA_TYPE = "video/mp4";
const HLS_MEDIA_TYPE = "application/vnd.apple.mpegurl";
const HLS_MEDIA_TYPES = new Set([
    HLS_MEDIA_TYPE,
    "application/x-mpegurl",
]);

function getAdMediaUrl(source) {
    if (typeof source === "string") return source.trim();
    if (!source || typeof source !== "object") return "";

    var url = source.src || source.url;
    return typeof url === "string" ? url.trim() : "";
}

function normalizeMediaType(type) {
    if (typeof type !== "string") return "";
    return type.split(";")[0].trim().toLowerCase();
}

function hasMediaExtension(url, extension) {
    return url.split(/[?#]/)[0].toLowerCase().endsWith(extension);
}

function inferAdMediaType(source, url) {
    var declaredType = normalizeMediaType(
        source && typeof source === "object"
            ? source.type || source.mimeType
            : ""
    );

    if (declaredType) {
        if (declaredType === MP4_MEDIA_TYPE) return MP4_MEDIA_TYPE;
        if (HLS_MEDIA_TYPES.has(declaredType)) return HLS_MEDIA_TYPE;
        return null;
    }

    if (hasMediaExtension(url, ".m3u8")) return HLS_MEDIA_TYPE;
    if (hasMediaExtension(url, ".mp4")) return MP4_MEDIA_TYPE;

    var delivery =
        source && typeof source === "object" && typeof source.delivery === "string"
            ? source.delivery.toLowerCase()
            : "";

    if (delivery === "streaming") return HLS_MEDIA_TYPE;

    // Staging currently returns extensionless progressive `/video` URLs.
    // Bare URLs are kept MP4-compatible for the same backend contract.
    if (!delivery || delivery === "progressive") return MP4_MEDIA_TYPE;

    return null;
}

/**
 * Determine whether a normalized or raw advertisement source is HLS.
 */
export function isHlsAdMediaSource(source) {
    var url = getAdMediaUrl(source);
    return !!url && inferAdMediaType(source, url) === HLS_MEDIA_TYPE;
}

/**
 * Normalize playable MP4 and HLS advertisement sources.
 *
 * Media metadata is retained until this boundary so extensionless staging
 * endpoints and streaming VAST media can both select the correct provider.
 */
export function normalizeAdMediaSources(sources) {
    if (!Array.isArray(sources)) return [];

    return sources
        .map(function (source) {
            var src = getAdMediaUrl(source);
            if (!src) return null;

            var type = inferAdMediaType(source, src);
            return type ? { src: src, type: type } : null;
        })
        .filter(Boolean);
}

function readCampaignKey(url) {
    if (!url || typeof url !== "string") return null;

    try {
        return new URL(url).searchParams.get("campaignKey");
    } catch (_error) {
        return null;
    }
}

/**
 * Pair aggregate response impressions with the creative they belong to.
 *
 * The advertisement API can return creatives from multiple campaigns inside
 * one Inline response. Its `impressions` array follows creative order and the
 * signed URLs also carry a campaignKey. Firing the entire array for the first
 * pre-roll falsely records later timed campaigns as pre-roll impressions.
 */
export function getCreativeImpressionUrls(
    impressionUrls,
    creative,
    responseIndex
) {
    if (!Array.isArray(impressionUrls) || impressionUrls.length === 0 || !creative) {
        return [];
    }

    var creativeCampaignKey = (creative.linear?.trackingEvents || [])
        .map(function (event) {
            return readCampaignKey(event?.url);
        })
        .find(Boolean);

    if (creativeCampaignKey) {
        var campaignMatches = impressionUrls.filter(function (url) {
            return readCampaignKey(url) === creativeCampaignKey;
        });
        if (campaignMatches.length > 0) return campaignMatches;
    }

    if (
        Number.isInteger(responseIndex) &&
        responseIndex >= 0 &&
        responseIndex < impressionUrls.length
    ) {
        return [impressionUrls[responseIndex]];
    }

    // A single legacy Inline impression belongs to the ad as a whole. Every
    // creative may receive it; the tracker session deduplicates the URL.
    return impressionUrls.length === 1 ? impressionUrls : [];
}

/**
 * Build a sorted ad queue from VAST JSON creatives
 *
 * @param {Array<object>} creatives - Array of VastCreative objects
 * @param {number} contentDurationSec - Content duration in seconds
 * @returns {Array<{queueId: string, creative: object, triggerTime: number, mediaFiles: object[], mediaUrls: string[], isPreRoll: boolean}>}
 */
export function buildAdQueue(creatives, contentDurationSec) {
    if (!creatives || !Array.isArray(creatives) || creatives.length === 0) {
        return [];
    }

    return creatives
        .map(function (creative, responseIndex) {
            var selectedMedia = selectMediaFiles(creative);
            if (selectedMedia.length === 0) return null;
            var urls = selectedMedia.map(function (media) {
                return media.url;
            });

            return {
                queueId: (creative.id || "creative") + ":" + responseIndex,
                responseIndex: responseIndex,
                creative: creative,
                triggerTime: getAdTriggerTime(creative, contentDurationSec),
                mediaFiles: selectedMedia,
                mediaUrls: urls,
                isPreRoll: creative.creativeSlot === "pre-roll",
                capacity: selectedMedia.length,
                randomMedia: !!creative.extRandomMedia,
            };
        })
        .filter(function (item) {
            return item !== null;
        })
        .sort(function (a, b) {
            return a.triggerTime - b.triggerTime || a.responseIndex - b.responseIndex;
        });
}

/**
 * Recalculate trigger times without reselecting or reshuffling media.
 */
export function scheduleAdQueue(queue, contentDurationSec) {
    if (!Array.isArray(queue)) return [];

    return queue
        .map(function (item) {
            var hasCustomTime =
                item.creative?.extCustomTime !== undefined &&
                item.creative?.extCustomTime !== null &&
                Number.isFinite(Number(item.creative.extCustomTime)) &&
                Number(item.creative.extCustomTime) >= 0;
            var canSchedule =
                item.isPreRoll || hasCustomTime || contentDurationSec > 0;

            return {
                ...item,
                triggerTime: canSchedule
                    ? getAdTriggerTime(item.creative, contentDurationSec)
                    : null,
            };
        })
        .sort(function (a, b) {
            var aTime = Number.isFinite(a.triggerTime) ? a.triggerTime : Infinity;
            var bTime = Number.isFinite(b.triggerTime) ? b.triggerTime : Infinity;
            return aTime - bTime || a.responseIndex - b.responseIndex;
        });
}

export function getNextDueAd(queue, playedQueueIds, currentTimeSec) {
    if (!Array.isArray(queue) || !Number.isFinite(currentTimeSec)) return null;

    var nextAd = getNextPendingTimedAd(queue, playedQueueIds);
    return nextAd && currentTimeSec >= nextAd.triggerTime ? nextAd : null;
}

/**
 * Return the next unplayed, scheduled content interruption.
 */
export function getNextPendingTimedAd(queue, playedQueueIds) {
    if (!Array.isArray(queue)) return null;

    return (
        queue.find(function (item) {
            return (
                !item.isPreRoll &&
                Number.isFinite(item.triggerTime) &&
                !playedQueueIds?.has(item.queueId)
            );
        }) || null
    );
}

/**
 * Calculate a media-time countdown for the next advertisement break.
 * Returning null outside the window keeps the UI dormant between breaks.
 */
export function getAdCountdown(
    queue,
    playedQueueIds,
    currentTimeSec,
    countdownWindowSec
) {
    if (!Number.isFinite(currentTimeSec)) return null;

    var windowSeconds = Number(countdownWindowSec);
    if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) return null;

    var nextAd = getNextPendingTimedAd(queue, playedQueueIds);
    if (!nextAd) return null;

    var remaining = nextAd.triggerTime - currentTimeSec;
    if (remaining <= 0 || remaining > windowSeconds) return null;

    return {
        ad: nextAd,
        secondsRemaining: Math.ceil(remaining),
    };
}
